import React, { useRef, useState } from 'react';
import { eFeePropertyNames } from 'types/order/fees/eFeePropertyNames';
import { ISettlementItemData } from 'types/order/fees/ISectionData';
import { assertDefined } from 'utilities/Assertions';
import { ConfirmDeleteDialog } from './ConfirmDeleteDialog';
import { IFeeSection } from './organizeFees';
import { ITableColumn } from './table/getColumns';
import { convertSettlementItemToLineItem } from './ILineItem';
import { useListAdminStandardDocumentsQuery } from '../../../API/order/documents/DocumentApi';
import { EditContainer } from './edit/EditContainer';
import { mergeDocumentsAsync } from 'API/order/documents/adminDocumentsAPI';
import { IDocumentCategory } from 'types/order/documents/IDocumentCategoryType';
import { FeeContainer } from './FeeContainer';
import { FeeTabs, clientUrls } from 'ui-toolkit/rest/ClientUrlFactory';
import { useParams } from 'react-router-dom';
import { useHasPermissions } from '../../accessControl/hooks/useHasPermissions';
import { ePermissions } from 'types/administration/userAdministration/ePermissions';

export interface IBaseFeeContainerProps {
  columns: ITableColumn[];
  fee: ISettlementItemData;
  feeSection: IFeeSection;
  index: number;
  layout: 'Single Column' | 'Two Column';
}

export const OrderFeeContainer: React.FC<IBaseFeeContainerProps> = (props) => {
  const [isDeleting, setDeleting] = useState<boolean>(false);

  const hasEditPermission = useHasPermissions([ePermissions.CdItemsV3Edit]);

  const { fee, feeSection, layout } = props;

  const feeDefinition = feeSection.feeDefinitions.find(
    (t) => t.statementSettlementAndFeeCategoryDefinitionId === fee.statementSettlementAndFeeCategoryDefinitionId
  );
  assertDefined(feeDefinition);

  const { orderId, section, feeId } = useParams<{ orderId: string; section: FeeTabs; feeId?: string }>();
  const showEdit = feeId !== undefined && feeId === fee.settlementStatementItemId;

  const { data: adminDocuments } = useListAdminStandardDocumentsQuery({ orderId }, { pollingInterval: 60 * 1000 });

  const lineItem = convertSettlementItemToLineItem(fee, adminDocuments);

  const onPrintAction = async (documents: string[]) => {
    if (adminDocuments?.length) {
      return await mergeDocumentsAsync(orderId, documents, fee.settlementStatementDescription + '-all', {
        name: adminDocuments[0].category,
      } as IDocumentCategory);
    }
  };

  const isEditable = hasEditPermission && feeDefinition.editable && feeSection.options.length > 0;
  const isDeletable = hasEditPermission && feeDefinition.editable;

  const item = useRef<HTMLDivElement>(null);

  return (
    <div ref={item} className={'scroll-mt-28'}>
      <FeeContainer
        {...props}
        fee={lineItem}
        isEditable={isEditable}
        editUrl={clientUrls.urlFor.feesTabEdit(orderId, section, fee.settlementStatementItemId)}
        onDelete={isDeletable ? () => setDeleting(true) : undefined}
        onPrint={onPrintAction}
      />
      {isDeleting && (
        <ConfirmDeleteDialog
          message={`fee ${fee.stringSettlementStatementPropertyValues[eFeePropertyNames.Name]}`}
          onDone={() => setDeleting(false)}
          fee={lineItem}
          orderId={orderId}
        />
      )}
      {showEdit && (
        <EditContainer
          layout={layout}
          lineItemId={lineItem.id}
          section={feeSection.sectionData}
          sectionDefinition={feeSection.sectionDefinition}
          orderId={orderId}
        />
      )}
    </div>
  );
};
